import { Currency } from "@redotech/money/currencies";
import { StoredShippingProviderTypeSchema } from "@redotech/redo-fulfillment-carrier-common/shipment";
import { createEnum } from "@redotech/zod-util/zod-enum";
import Big from "big.js";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "../draft-return/date-schema";

export const RETURN_CARRIER_QUOTE_TTL = Temporal.Duration.from({ minutes: 30 });

export const ReturnCarrierOptionSchema = z.object({
  quoteVersion: z.literal(1),
  id: z.string(),
  carrier: z.string(),
  service: z.string(),
  quotedFee: z.string(),
  currency: createEnum(Currency),
  shippingProviderType: StoredShippingProviderTypeSchema,
  // CongoDB returns embedded dates as ISO strings on a lean read.
  quotedAt: DateFromDateOrIsoStringSchema,
  expiresAt: DateFromDateOrIsoStringSchema,
  isMerchantCarrier: z.boolean(),
});
export type ReturnCarrierOption = z.infer<typeof ReturnCarrierOptionSchema>;

export const ReturnCarrierSelectionSchema = z.object({
  coveredOption: ReturnCarrierOptionSchema,
  selectedOption: ReturnCarrierOptionSchema,
  upgradeFee: z.string(),
});
export type ReturnCarrierSelection = z.infer<
  typeof ReturnCarrierSelectionSchema
>;

/** Only for parsed or Mongoose-hydrated options; a lean CongoDB read holds strings. */
export function optionExpiresAt(option: { expiresAt: Date }): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(option.expiresAt.getTime());
}

export function sortReturnCarrierOptionsByQuotedFee(
  options: ReturnCarrierOption[],
): ReturnCarrierOption[] {
  return [...options].sort((left, right) =>
    Big(left.quotedFee).cmp(right.quotedFee),
  );
}

export function getReturnCarrierUpgradeFee({
  coveredOption,
  selectedOption,
}: {
  coveredOption: ReturnCarrierOption;
  selectedOption: ReturnCarrierOption;
}): string {
  const difference = Big(selectedOption.quotedFee).minus(
    coveredOption.quotedFee,
  );
  return (difference.gt(0) ? difference : Big(0)).toFixed(2);
}

export function resolveReturnCarrierSelection({
  options,
  coveredOptionId,
  selectedOptionId,
  now = Temporal.Now.instant(),
}: {
  options: ReturnCarrierOption[];
  coveredOptionId: string | null | undefined;
  selectedOptionId: string | null | undefined;
  now?: Temporal.Instant;
}): ReturnCarrierSelection | undefined {
  if (!coveredOptionId || !selectedOptionId) {
    return undefined;
  }
  const coveredOption = options.find(({ id }) => id === coveredOptionId);
  const selectedOption = options.find(({ id }) => id === selectedOptionId);
  if (
    !coveredOption ||
    !selectedOption ||
    Temporal.Instant.compare(optionExpiresAt(coveredOption), now) <= 0 ||
    Temporal.Instant.compare(optionExpiresAt(selectedOption), now) <= 0
  ) {
    return undefined;
  }
  return {
    coveredOption,
    selectedOption,
    upgradeFee: getReturnCarrierUpgradeFee({ coveredOption, selectedOption }),
  };
}

export function isIncludeMultipleCarrierOptions(team: {
  settings?: { includeMultipleCarrierOptions?: boolean | null };
}): boolean {
  return team.settings?.includeMultipleCarrierOptions === true;
}

/** Draft shipment whose `fee`, `carrier` and `rateSource` describe the selected rate. */
interface CarrierSelectableShipment {
  fee: string;
  carrier: string;
  rateSource?: {
    isMerchantCarrier?: boolean | null;
    shippingProviderType?: string | null;
  } | null;
  carrierOptions?: unknown;
  coveredCarrierOptionId?: string | null;
  selectedCarrierUpgradeFee?: string | null;
}

function parseReturnCarrierOptions(value: unknown): ReturnCarrierOption[] {
  const parsed = ReturnCarrierOptionSchema.array().safeParse(value ?? []);
  return parsed.success ? parsed.data : [];
}

export function getCoveredReturnCarrierOption(
  shipment: Pick<
    CarrierSelectableShipment,
    "carrierOptions" | "coveredCarrierOptionId"
  >,
): ReturnCarrierOption | undefined {
  return parseReturnCarrierOptions(shipment.carrierOptions).find(
    ({ id }) => id === shipment.coveredCarrierOptionId,
  );
}

/** Options are the cheapest rate per carrier, so `carrier` identifies the selection. */
export function getSelectedReturnCarrierOption(
  shipment: Pick<CarrierSelectableShipment, "carrierOptions" | "carrier">,
): ReturnCarrierOption | undefined {
  return parseReturnCarrierOptions(shipment.carrierOptions).find(
    ({ carrier }) => carrier === shipment.carrier,
  );
}

export function applyReturnCarrierSelection(
  shipment: CarrierSelectableShipment,
  { selectedOption, upgradeFee }: ReturnCarrierSelection,
): void {
  shipment.fee = selectedOption.quotedFee;
  shipment.carrier = selectedOption.carrier;
  if (shipment.rateSource) {
    shipment.rateSource.isMerchantCarrier = selectedOption.isMerchantCarrier;
    shipment.rateSource.shippingProviderType =
      selectedOption.shippingProviderType;
  } else {
    shipment.rateSource = {
      isMerchantCarrier: selectedOption.isMerchantCarrier,
      shippingProviderType: selectedOption.shippingProviderType,
    };
  }
  shipment.selectedCarrierUpgradeFee = upgradeFee;
}

export function resetReturnCarrierSelectionToCovered(
  shipment: CarrierSelectableShipment,
): void {
  const coveredOption = getCoveredReturnCarrierOption(shipment);
  if (coveredOption) {
    applyReturnCarrierSelection(shipment, {
      coveredOption,
      selectedOption: coveredOption,
      upgradeFee: "0.00",
    });
  }
}

/** Label fee to charge; stays at the covered option until upgrade charging lands (INTR-128). */
export function getChargeableShipmentFee(
  shipment: Pick<
    CarrierSelectableShipment,
    "fee" | "rateSource" | "carrierOptions" | "coveredCarrierOptionId"
  >,
): { fee: string; isMerchantCarrier: boolean } {
  const coveredOption = getCoveredReturnCarrierOption(shipment);
  return coveredOption
    ? {
        fee: coveredOption.quotedFee,
        isMerchantCarrier: coveredOption.isMerchantCarrier,
      }
    : {
        fee: shipment.fee,
        isMerchantCarrier: !!shipment.rateSource?.isMerchantCarrier,
      };
}

export function stripReturnCarrierOptionFields<
  T extends {
    carrierOptions?: unknown;
    coveredCarrierOptionId?: unknown;
    selectedCarrierUpgradeFee?: unknown;
  },
>(shipment: T): T {
  return {
    ...shipment,
    carrierOptions: undefined,
    coveredCarrierOptionId: undefined,
    selectedCarrierUpgradeFee: undefined,
  };
}

export function hasInvalidReturnCarrierSelection({
  shipments,
  now = Temporal.Now.instant(),
}: {
  shipments:
    | Pick<
        CarrierSelectableShipment,
        "carrier" | "carrierOptions" | "coveredCarrierOptionId"
      >[]
    | null
    | undefined;
  now?: Temporal.Instant;
}): boolean {
  return (
    shipments?.some((shipment) => {
      // A shipment that never minted a covered option has nothing to upgrade
      // from, so it can never carry an invalid selection.
      if (!shipment.coveredCarrierOptionId) {
        return false;
      }
      const selectedOption = getSelectedReturnCarrierOption(shipment);
      // The default covered selection is Redo's own rate, so an expired quote
      // must not block submit.
      if (
        !selectedOption ||
        selectedOption.id === shipment.coveredCarrierOptionId
      ) {
        return false;
      }
      return !resolveReturnCarrierSelection({
        options: parseReturnCarrierOptions(shipment.carrierOptions),
        coveredOptionId: shipment.coveredCarrierOptionId,
        selectedOptionId: selectedOption.id,
        now,
      });
    }) ?? false
  );
}
